/** Manages player key bindings for all game actions, with persist/load support. */
import Phaser from 'phaser';
import { SaveSystem } from './SaveSystem';

/** All player-remappable actions; each entry maps to a primary and optional alternate key code. */
export enum GameAction {
  MoveUp = 'moveUp',
  MoveDown = 'moveDown',
  MoveLeft = 'moveLeft',
  MoveRight = 'moveRight',
  Run = 'run',
  Dash = 'dash',
  Pause = 'pause',
  Skill1 = 'skill1',
  Skill2 = 'skill2',
  Skill3 = 'skill3',
  Skill4 = 'skill4',
  Skill5 = 'skill5',
}

/** A single action's key assignment: one required primary key code and one optional alternate. */
export interface ActionBinding {
  primary: number;
  alternate?: number;
}

/** Complete mapping from every GameAction to its ActionBinding; the authoritative key layout at runtime. */
export type KeyMap = Record<GameAction, ActionBinding>;

type SerializedKeyMap = Record<string, { primary: number; alternate?: number }>;

const ACTION_LABELS: Record<GameAction, string> = {
  [GameAction.MoveUp]: 'Move Up',
  [GameAction.MoveDown]: 'Move Down',
  [GameAction.MoveLeft]: 'Move Left',
  [GameAction.MoveRight]: 'Move Right',
  [GameAction.Run]: 'Run',
  [GameAction.Dash]: 'Dash',
  [GameAction.Pause]: 'Pause',
  [GameAction.Skill1]: 'Skill 1',
  [GameAction.Skill2]: 'Skill 2',
  [GameAction.Skill3]: 'Skill 3',
  [GameAction.Skill4]: 'Skill 4',
  [GameAction.Skill5]: 'Skill 5',
};

const DEFAULT_KEY_MAP: KeyMap = {
  [GameAction.MoveUp]: { primary: Phaser.Input.Keyboard.KeyCodes.E, alternate: Phaser.Input.Keyboard.KeyCodes.UP },
  [GameAction.MoveDown]: { primary: Phaser.Input.Keyboard.KeyCodes.D, alternate: Phaser.Input.Keyboard.KeyCodes.DOWN },
  [GameAction.MoveLeft]: { primary: Phaser.Input.Keyboard.KeyCodes.S, alternate: Phaser.Input.Keyboard.KeyCodes.LEFT },
  [GameAction.MoveRight]: { primary: Phaser.Input.Keyboard.KeyCodes.F, alternate: Phaser.Input.Keyboard.KeyCodes.RIGHT },
  [GameAction.Run]: { primary: Phaser.Input.Keyboard.KeyCodes.SHIFT },
  [GameAction.Dash]: { primary: Phaser.Input.Keyboard.KeyCodes.SPACE },
  [GameAction.Pause]: { primary: Phaser.Input.Keyboard.KeyCodes.ESC },
  [GameAction.Skill1]: { primary: Phaser.Input.Keyboard.KeyCodes.J, alternate: Phaser.Input.Keyboard.KeyCodes.ONE },
  [GameAction.Skill2]: { primary: Phaser.Input.Keyboard.KeyCodes.K, alternate: Phaser.Input.Keyboard.KeyCodes.TWO },
  [GameAction.Skill3]: { primary: Phaser.Input.Keyboard.KeyCodes.L, alternate: Phaser.Input.Keyboard.KeyCodes.THREE },
  [GameAction.Skill4]: { primary: Phaser.Input.Keyboard.KeyCodes.SEMICOLON, alternate: Phaser.Input.Keyboard.KeyCodes.FOUR },
  [GameAction.Skill5]: { primary: Phaser.Input.Keyboard.KeyCodes.QUOTES, alternate: Phaser.Input.Keyboard.KeyCodes.FIVE },
};

/** Sentinel value for an unbound action (no valid key code). */
const UNBOUND = -1;

// Reverse lookup cache: keyCode → key name
const KEY_NAMES = new Map<number, string>();
for (const [name, code] of Object.entries(Phaser.Input.Keyboard.KeyCodes)) {
  if (typeof code === 'number') {
    KEY_NAMES.set(code, name);
  }
}

/** Static registry of all player key bindings; persists to and loads from SaveSystem. */
export class KeyBindings {
  private static currentMap: KeyMap = KeyBindings.cloneMap(DEFAULT_KEY_MAP);
  private static saveSystem = new SaveSystem();

  /** Load bindings from localStorage. Call once at startup. */
  static load(): void {
    const settings = this.saveSystem.loadSettings();
    const saved = settings.keyBindings as SerializedKeyMap | undefined;
    if (!saved) return;

    for (const action of Object.values(GameAction)) {
      if (saved[action]) {
        this.currentMap[action] = {
          primary: saved[action].primary,
          alternate: saved[action].alternate,
        };
      }
    }
  }

  /** Persist current bindings to localStorage. */
  static save(): void {
    const settings = this.saveSystem.loadSettings();
    settings.keyBindings = this.serialize();
    this.saveSystem.saveSettings(settings);
  }

  /** Reset all bindings to defaults. */
  static resetToDefaults(): void {
    this.currentMap = this.cloneMap(DEFAULT_KEY_MAP);
  }

  /** Get the binding for a specific action. */
  static getBinding(action: GameAction): Readonly<ActionBinding> {
    return this.currentMap[action];
  }

  /** Get the full map (read-only). */
  static getAll(): Readonly<KeyMap> {
    return this.currentMap;
  }

  /** Set primary key for an action. Returns conflicting action if any. */
  static setPrimary(action: GameAction, keyCode: number): GameAction | null {
    const conflict = this.findConflict(action, keyCode);
    if (conflict) {
      this.unbindKey(conflict, keyCode);
    }
    const binding = this.currentMap[action];
    if (binding.alternate === undefined && binding.primary >= 0) {
      binding.alternate = binding.primary;
    }
    binding.primary = keyCode;
    return conflict;
  }

  /** Set alternate key for an action. */
  static setAlternate(action: GameAction, keyCode: number | undefined): GameAction | null {
    if (keyCode === undefined) {
      this.currentMap[action].alternate = undefined;
      return null;
    }
    const conflict = this.findConflict(action, keyCode);
    if (conflict) {
      this.unbindKey(conflict, keyCode);
    }
    this.currentMap[action].alternate = keyCode;
    return conflict;
  }

  /** Remove a specific key code from an action's bindings. */
  private static unbindKey(action: GameAction, keyCode: number): void {
    const binding = this.currentMap[action];
    if (binding.primary === keyCode && binding.alternate !== undefined) {
      // Promote alternate to primary
      binding.primary = binding.alternate;
      binding.alternate = undefined;
    } else if (binding.primary === keyCode) {
      // No alternate — action becomes unbound
      binding.primary = UNBOUND;
    } else if (binding.alternate === keyCode) {
      binding.alternate = undefined;
    }
  }

  /** Human-readable name for a key code. */
  static keyLabel(keyCode: number): string {
    if (keyCode === UNBOUND) return '---';
    return KEY_NAMES.get(keyCode) ?? `KEY_${keyCode}`;
  }

  /** Display string for an action's bindings (e.g. "W / UP"). */
  static displayLabel(action: GameAction): string {
    const binding = this.currentMap[action];
    const primary = this.keyLabel(binding.primary);
    if (binding.alternate !== undefined) {
      return `${primary} / ${this.keyLabel(binding.alternate)}`;
    }
    return primary;
  }

  /** User-friendly name for a GameAction. */
  static actionLabel(action: GameAction): string {
    return ACTION_LABELS[action];
  }

  private static findConflict(forAction: GameAction, keyCode: number): GameAction | null {
    for (const action of Object.values(GameAction)) {
      if (action === forAction) continue;
      const binding = this.currentMap[action];
      if (binding.primary === keyCode || binding.alternate === keyCode) {
        return action;
      }
    }
    return null;
  }

  private static serialize(): SerializedKeyMap {
    const result: SerializedKeyMap = {};
    for (const action of Object.values(GameAction)) {
      result[action] = { ...this.currentMap[action] };
    }
    return result;
  }

  private static cloneMap(map: KeyMap): KeyMap {
    const clone = {} as KeyMap;
    for (const action of Object.values(GameAction)) {
      clone[action] = { ...map[action] };
    }
    return clone;
  }
}
